import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { TextField, Chip, useTheme } from '@mui/material';
import { fonts } from '@theme/fontsCustomer';
import { CloseIcon } from '@icons/CloseIcon';
import type { ExpressionType } from '@repositories/workflowRepository/workflow.types';
import { useGetSoftwareList } from '@queryHooks/useSoftware';
import { useGetSubscriptionList } from '@queryHooks/useSubscription';

interface Props {
  value: string;
  expressionType?: ExpressionType;
  isEditable: boolean;
  onSelect: (cursorPos?: number) => void;
  onChange: (value: string) => void;
}

export const ResourceValueInput = ({ value, expressionType, isEditable, onSelect, onChange }: Props) => {
  const { t } = useTranslation();
  const isCustom = expressionType === 'CUSTOM';

  // SPEL 표현식인지 확인
  const isSpelExpression = useMemo(() => {
    return value ? /^#{[^}]+}$/.test(value.trim()) : false;
  }, [value]);

  // 리소스 ID 여부 확인
  const hasResourceId = useMemo(() => {
    if (!value || isSpelExpression || isCustom) return false;
    return value.trim().length > 0;
  }, [value, isSpelExpression, isCustom]);

  // 구독과 제품 목록 모두 조회 (React Query 캐싱 활용)
  const { data: subscriptions } = useGetSubscriptionList(undefined, {
    enabled: hasResourceId,
  });

  const { data: products } = useGetSoftwareList(
    {},
    {
      enabled: hasResourceId,
    },
  );

  const theme = useTheme();

  // 리소스 name 가져오기 (구독 또는 제품에서 검색)
  const resourceName = useMemo(() => {
    if (!value || isSpelExpression) return null;

    // 구독 목록에서 검색
    const subscription = subscriptions?.content?.find(s => s.id === value);
    if (subscription) return subscription.name;

    // 제품 목록에서 검색
    const product = products?.content?.find(p => p.id === value);
    if (product) return product.name;

    return null;
  }, [value, isSpelExpression, subscriptions, products]);

  // CUSTOM 모드이거나 SPEL 표현식: TextField로 표시
  if (isCustom || isSpelExpression) {
    return (
      <TextField
        value={value}
        fullWidth
        multiline
        placeholder={t('Workflow.action.select_value')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (!isEditable) return;
          onChange(e.target.value);
        }}
        onSelect={(e: React.SyntheticEvent<HTMLDivElement>) => {
          if (!isEditable) return;
          const target = e.target as HTMLInputElement;
          onSelect(target.selectionStart ?? undefined);
        }}
        autoComplete="off"
        InputProps={{
          readOnly: !isEditable,
        }}
      />
    );
  }

  // SIMPLE 모드이고 리소스 ID: 칩으로 name 표시
  if (resourceName) {
    return (
      <ChipContainer onClick={() => isEditable && onSelect(0)} isEditable={isEditable}>
        <StyledChip
          label={resourceName}
          size="small"
          onDelete={
            isEditable
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onChange('');
                }
              : undefined
          }
          deleteIcon={
            <DeleteIconWrapper>
              <CloseIcon width={14} height={14} color={theme.colors['ic-gray-main']} />
            </DeleteIconWrapper>
          }
        />
      </ChipContainer>
    );
  }

  // 값이 없는 경우: placeholder 표시
  return (
    <ChipContainer onClick={() => isEditable && onSelect(0)} isEditable={isEditable}>
      <PlaceholderText>{t('Workflow.action.select_value')}</PlaceholderText>
    </ChipContainer>
  );
};

const ChipContainer = styled.div<{ isEditable: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors['border-gray-light']};
  border-radius: 4px;
  background-color: ${props => props.theme.colors['bg-white']};
  cursor: ${props => (props.isEditable ? 'pointer' : 'default')};

  &:hover {
    border-color: ${props =>
      props.isEditable ? props.theme.colors['border-gray-dark'] : props.theme.colors['border-gray-light']};
  }
`;

const PlaceholderText = styled.span`
  ${fonts.Body2};
  color: ${props => props.theme.colors['text-gray-light']};
`;

const StyledChip = styled(Chip)`
  max-width: 100%;
  background-color: ${props => props.theme.colors['bg-primary-lighter']};
  color: ${props => props.theme.colors['text-primary']};

  & .MuiChip-label {
    ${fonts.Body2};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .MuiChip-deleteIcon {
    margin-right: 4px;
    pointer-events: auto;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }
`;

const DeleteIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: pointer;
`;
